import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { setPopup, setCrmSelectedTemplate } from '@src/state/popupAtoms';
import { useAtomValue } from 'jotai';
import { crmSelectedTemplateAtom } from '@src/state/popupAtoms';
import { EPopup } from '@src/types/common';
import { useMutationMessageBatchCreate } from '@src/queries/messaging/useMutationMessageBatchCreate';
import { style } from 'typestyle';
import dayjs from 'dayjs';
import { ADALONG_COLORS } from '@src/styles';
import { EnrichedRightRequest } from 'common';
import { ActionButton } from '@src/components/ActionButton';
import { ConfirmationOverlay } from '@src/pages/Widget/Edit/Settings/subcomponents/ConfirmationOverlay';
import { Modal } from '@src/components/Modal';
import { ConversationsTable } from './ConversationsTable';
import { processTemplateContent } from '@src/utils/crmTemplates';
import { useQueryUserConfig } from '@src/queries/users/useQueryUserConfig';
import { useConversationDataManager } from '@src/hooks/CRM/useConversationDataManager';
import { useConversationActions } from '@src/hooks/CRM/useConversationActions';

const getTimestamp = (value?: string | null): number | null => {
  if (!value) return null;
  const time = new Date(value).getTime();
  return Number.isNaN(time) ? null : time;
};

type ConversationsGridProps = {
  onConversationChange?: (targetId: string, interlocutorId: string, conversationId: string) => void;
  hasSelectedConversation?: boolean;
};

export const ConversationsGrid: FC<ConversationsGridProps> = ({
  onConversationChange,
  hasSelectedConversation = false,
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // 0: With Rights Agreed (default), 1: Recent activity, 2: Favorites, 3: All
  const [isRefreshing, setIsRefreshing] = useState(false); 
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null); 
  const [checkedRows, setCheckedRows] = useState<Array<{ conversationId: string; interlocutorId: string; senderId: string }>>([]); // For multi-select
  const [showBatchSendConfirm, setShowBatchSendConfirm] = useState(false);
  // Track if user has used the template selector before
  const [hasUsedTemplateSelector, setHasUsedTemplateSelector] = useState(
    typeof window !== 'undefined' ? localStorage.getItem('hasUsedTemplateSelector') === 'true' : false
  );

  // Filter and sort state 
  const [filters, setFilters] = useState({
    username: [] as string[],
    language: [] as string[],
    type: [] as string[],
    rightsAgreed: [] as string[],
    favorite: [] as string[],
  });
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const { data: userConfig } = useQueryUserConfig();
  const crmSelectedTemplate = useAtomValue(crmSelectedTemplateAtom);
  const mutationBatchCreate = useMutationMessageBatchCreate();

  // Single source of truth for all conversation data
  const {
    conversations,
    isLoading,
    error,
    refetch,
    isOnline,
    loadMoreStoredConversations,
    hasMoreStoredConversations,
    isFetchingMoreStoredConversations,
  } = useConversationDataManager();

  const handleLoadMore = useCallback(() => {
    if (!hasMoreStoredConversations || isFetchingMoreStoredConversations) {
      return;
    }
    loadMoreStoredConversations();
  }, [hasMoreStoredConversations, isFetchingMoreStoredConversations, loadMoreStoredConversations]);

  // Backend-based conversation actions (replaces localStorage)
  const { markConversationAsRead, toggleConversationFavorite } = useConversationActions();

  // Listen for template selection and show confirmation popin
  useEffect(() => {
    if (crmSelectedTemplate && checkedRows.length > 0) {
      setSelectedTemplateId(crmSelectedTemplate._id); // Track selected template
      setShowBatchSendConfirm(true);
    }
  }, [crmSelectedTemplate, checkedRows]);

  // UGC preview renderer 
  const renderUGCCell = (enrichedRightRequests: EnrichedRightRequest[] | undefined) => {
    const thumbnails = enrichedRightRequests?.flatMap(data => data.medias?.map(media => media.url.thumbnail) ?? [])
      .slice(0, 3);

    return (
      <div className={ugcContainerClass}>
        {thumbnails?.length ? (
          thumbnails.map((thumbnail, index) => (
            <img
              key={index}
              className={ugcImageClass}
              src={thumbnail}
              alt={`UGC Thumbnail ${index + 1}`}
            />
          ))
        ) : (
          <div style={{ color: '#888', fontSize: '12px' }}>N/A</div>
        )}
      </div>
    );
  };

  // Rights Agreed status renderer
  const renderIPRCell = (hasIPR: boolean, iprCount: number) => {
    return hasIPR ? (
      <div style={{ color: ADALONG_COLORS.GREEN, fontWeight: 'bold' }}>
        {iprCount}
      </div>
    ) : (
      <div style={{ color: '#888' }}>-</div>
    );
  };


  // Filter conversations by type
  const { iprConversations, recentConversations, favoriteConversationsFiltered, allConversations } = useMemo(() => {
    const ipr = conversations.filter(conv => conv.hasIPR);
    // Recent activity: sort by lastActivityDate and take conversations from last 30 days
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const thirtyDaysAgoTs = thirtyDaysAgo.getTime();
    const recent = conversations
      .filter(conv => {
        const timestamp = getTimestamp(conv.lastActivityDate);
        return timestamp !== null && timestamp >= thirtyDaysAgoTs;
      })
      .sort((a, b) => {
        const timeA = getTimestamp(a.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
        const timeB = getTimestamp(b.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
        return timeB - timeA;
      });
    
    // Favorites: filter conversations that are marked as favorites in the backend
    const favorites = conversations.filter(conv => conv.isFavorite);
    
    return {
      iprConversations: ipr,
      recentConversations: recent,
      favoriteConversationsFiltered: favorites,
      allConversations: conversations,
    };
  }, [conversations]);

  // Get current data based on active tab - memoized to prevent unnecessary recalculations
  const currentConversations = useMemo(() => {
    switch (activeTab) {
      case 0: return iprConversations; // With Rights Agreed
      case 1: return recentConversations; // Recent activity  
      case 2: return favoriteConversationsFiltered; // Favorites
      case 3: return allConversations; // All
      default: return iprConversations;
    }
  }, [activeTab, iprConversations, recentConversations, favoriteConversationsFiltered, allConversations]);

  // Apply filters and sorting 
  const filteredAndSortedConversations = useMemo(() => {
    let filtered = [...currentConversations];

    // Apply filters
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        filtered = filtered.filter(conv => {
          switch (key) {
            case 'username':
              return values.includes(conv.interlocutorUsername || '');
            case 'language':
              return values.includes(conv.language || 'N/A');
            case 'favorite':
              return values.includes(conv.isFavorite ? 'Yes' : 'No');
            case 'type':
              return values.includes('DM');
            case 'rightsAgreed':
              return values.includes(conv.hasIPR ? 'Yes' : 'No');
            default:
              return true;
          }
        });
      }
    });

    // Apply sorting
    filtered.sort((a, b) => {
      let valueA: any, valueB: any;
      
      switch (sortBy) {
        case 'date':
          valueA = getTimestamp(a.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
          valueB = getTimestamp(b.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
          break;
        case 'username':
          valueA = (a.interlocutorUsername || '').toLowerCase();
          valueB = (b.interlocutorUsername || '').toLowerCase();
          break;
        case 'language':
          valueA = (a.language || 'N/A').toLowerCase();
          valueB = (b.language || 'N/A').toLowerCase();
          break;
        case 'favorite':
          valueA = a.isFavorite ? 1 : 0;
          valueB = b.isFavorite ? 1 : 0;
          break;
        case 'rightsAgreed':
          valueA = a.hasIPR ? 1 : 0;
          valueB = b.hasIPR ? 1 : 0;
          break;
        default:
          valueA = getTimestamp(a.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
          valueB = getTimestamp(b.lastActivityDate) ?? Number.NEGATIVE_INFINITY;
      }

      if (valueA < valueB) return sortDirection === 'asc' ? -1 : 1;
      if (valueA > valueB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [currentConversations, filters, sortBy, sortDirection]);

  // Generate filter options from conversations
  const getFilterOptions = (field: string) => {
    const values = new Set<string>();
    currentConversations.forEach(conv => {
      let value = '';
      switch (field) {
        case 'username':
          value = conv.interlocutorUsername || '';
          break;
        case 'language':
          value = conv.language || 'N/A';
          break;
        case 'favorite':
          value = conv.isFavorite ? 'Yes' : 'No';
          break;
        case 'type':
          value = 'DM';
          break;
        case 'rightsAgreed':
          value = conv.hasIPR ? 'Yes' : 'No';
          break;
      }
      if (value) values.add(value);
    });
    
    return Array.from(values).sort().map(value => ({
      value,
      label: value,
      selected: filters[field as keyof typeof filters].includes(value),
    }));
  };

  const handleFilterChange = (field: string, selected: string[]) => {
    setFilters(prev => ({
      ...prev,
      [field]: selected,
    }));
  };

  const handleSortChange = (newSortBy: string, direction: 'asc' | 'desc') => {
    setSortBy(newSortBy);
    setSortDirection(direction);
  };

  // Handle refresh with local loading state
  const handleRefresh = async () => {
    if (isRefreshing) return; 
    
    setIsRefreshing(true);
    try {
      await refetch();
    } catch (error) {
      console.error('Failed to refresh conversations:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Handler for confirming batch send
  const handleConfirmBatchSend = () => {
    if (!crmSelectedTemplate || checkedRows.length === 0) return;
    const { processedContent } = processTemplateContent({ 
      content: crmSelectedTemplate.content, 
      currentTemplate: crmSelectedTemplate,
      termsUrl: crmSelectedTemplate.terms || userConfig?.user?.group?.terms_url,
    });
    const peers = checkedRows.map(r => ({
      interlocutorId: r.interlocutorId,
      senderAccountId: r.senderId,
    }));
    mutationBatchCreate.mutate({
      body: processedContent,
      networkName: 'instagram',
      peers,
    });
    setCheckedRows([]);
    setCrmSelectedTemplate(null);
    setShowBatchSendConfirm(false);
  };

  // Handler for cancelling batch send
  const handleCancelBatchSend = () => {
    setShowBatchSendConfirm(false);
    setCrmSelectedTemplate(null);
    setSelectedTemplateId(null); // Clear template ID
  };

  // Handler for toggling favorite status
  const handleToggleFavorite = useCallback(async (conversationId: string) => {
    // Find the conversation to get the current favorite status and IDs
    const conversation = conversations.find(conv => conv.id === conversationId);
    if (!conversation) return;

    try {
      await toggleConversationFavorite(
        conversation.targetId,
        conversation.interlocutorId,
        !conversation.isFavorite // Toggle the current status
      );
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  }, [conversations, toggleConversationFavorite]);

  // Convert conversations to grid rows - now using backend data
  const gridRows = useMemo(() => {
    return filteredAndSortedConversations.map(conv => {
      const conversationTimestamp = getTimestamp(conv.lastActivityDate);
      const formattedDate =
        conversationTimestamp !== null && conv.lastActivityDate
          ? dayjs(conv.lastActivityDate).format('DD/MM/YYYY HH:mm')
          : 'Unknown';

      return {
        id: conv.id,
        date: formattedDate,
        username: conv.interlocutorUsername,
        ugc: conv.ugcCount || 0,
        enrichedRightRequests: conv.enrichedRightRequests || [], // Pass enriched data for UGC rendering
        language: conv.language || 'N/A', 
        targetId: conv.targetId,
        interlocutorId: conv.interlocutorId,
        type: 'DM', 
        message: conv.lastMessage, 
        ipr: conv.hasIPR ? `${conv.iprCount} Rights Agreed` : '-', 
        diffusion: conv.diffusionCount || 0,
        hasIPR: conv.hasIPR,
        iprCount: conv.iprCount,
        conversationType: conv.type,
        hasUnread: typeof conv.isUnread === 'boolean' ? conv.isUnread : false,
        isFavorite: typeof conv.isFavorite === 'boolean' ? conv.isFavorite : false,
      };
    });
  }, [filteredAndSortedConversations]); 

  const handleRowClick = (row: any) => {
    setSelectedRowId(row.id);
    
    markConversationAsRead(row.targetId, row.interlocutorId);
    
    if (onConversationChange) {
      onConversationChange(
        row.targetId,
        row.interlocutorId,
        row.id,
      );
    }
  };

  // Checkbox selection logic
  const handleCheckboxChange = (
    info: { conversationId: string; interlocutorId: string; senderId: string },
    checked: boolean
  ) => {
    setCheckedRows(prev => {
      if (checked) {
        // Add if not already present
        if (!prev.some(r => r.conversationId === info.conversationId)) {
          return [...prev, info];
        }
        return prev;
      } else {
        // Remove by rowId
        return prev.filter(r => r.conversationId !== info.conversationId);
      }
    });
  };

  const handleEnrollInCampaign = () => {
    // Open the template selector popup, similar to MessageWriter
    if (!hasUsedTemplateSelector) {
      setHasUsedTemplateSelector(true);
      if (typeof window !== 'undefined') {
        localStorage.setItem('hasUsedTemplateSelector', 'true');
      }
    }
    setPopup(EPopup.templateSelector, {
      filterType: 'DM',
      selectedTemplateId: selectedTemplateId || undefined,
    });
  };

  if (error) {
    return (
      <div className={containerClass}>
        <div className={errorAlertClass}>
          <div className={alertTitleClass}>Failed to load conversations</div>
          <div className={alertMessageClass}>{error}</div>
          <div style={{ marginTop: '12px' }}>
            <ActionButton
              variant="transparent"
              size="small"
              onAction={refetch}
            >
              Retry
            </ActionButton>
          </div>
        </div>
      </div>
    );
  }

  if (!isOnline) {
    return (
      <div className={containerClass}>
        <div className={warningAlertClass}>
          <div className={alertTitleClass}>You're offline</div>
          <div className={alertMessageClass}>
            Showing cached conversations. Connect to internet for latest updates.
          </div>
        </div>
        {conversations.length > 0 ? (
          <ConversationsTable
            rows={gridRows}
            isCompact={hasSelectedConversation}
            onRowClick={handleRowClick}
            isLoading={isLoading}
            isFetchingMore={isFetchingMoreStoredConversations}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            renderUGCCell={renderUGCCell}
            renderIPRCell={renderIPRCell}
            getFilterOptions={getFilterOptions}
            onFilterChange={handleFilterChange}
            selectedRowId={selectedRowId}
            checkedRows={checkedRows}
            onCheckboxChange={handleCheckboxChange}
            onToggleFavorite={handleToggleFavorite}
            onLoadMore={handleLoadMore}
          />
        ) : (
          <ConversationsTable
            rows={[]}
            isCompact={hasSelectedConversation}
            onRowClick={handleRowClick}
            isLoading={isLoading}
            isFetchingMore={isFetchingMoreStoredConversations}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            renderUGCCell={renderUGCCell}
            renderIPRCell={renderIPRCell}
            getFilterOptions={getFilterOptions}
            onFilterChange={handleFilterChange}
            selectedRowId={selectedRowId}
            onToggleFavorite={handleToggleFavorite}
            onLoadMore={handleLoadMore}
          />
        ) }
      </div>
    );
  }

  return (
    <div className={containerClass}>
      {/* Confirmation Popin */}
      {showBatchSendConfirm && crmSelectedTemplate && (
        <Modal onClose={handleCancelBatchSend}>
          <ConfirmationOverlay
            message={
              `You are about to send a message to ${checkedRows.length} recipients using the template "${crmSelectedTemplate.name}".`
            }
            onCancel={handleCancelBatchSend}
            onConfirm={handleConfirmBatchSend}
            cancelText="Cancel"
            confirmText="Yes, send"
          />
        </Modal>
      )}
      <div className={headerClass}>
        <div className={titleContainerClass}>
          <h2 className={titleClass}>
            Conversations ({conversations.length})
          </h2>
          <p className={subtitleClass}>
            Manage your Instagram conversations and Rights requests.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <ActionButton
            variant="transparent"
            size="small"
            onAction={handleRefresh}
            disabled={isRefreshing || isLoading}
          >
            {isRefreshing || isLoading ? (
              'Refreshing...'
            ) : (
              'Refresh'
            )}
          </ActionButton>
          <ActionButton
            variant="transparent"
            size="small"
            onAction={handleEnrollInCampaign}
            disabled={checkedRows.length === 0}
          >
            Enroll in campaign
          </ActionButton>
        </div>
      </div>

      <div className={tabsContainerClass}>
        <div className={tabsListClass}>
          <button
            className={activeTab === 0 ? activeTabClass : tabClass}
            onClick={() => setActiveTab(0)}
          >
            With Rights Agreed ({iprConversations.length})
          </button>
          <button
            className={activeTab === 1 ? activeTabClass : tabClass}
            onClick={() => setActiveTab(1)}
          >
            Recent Activity ({recentConversations.length})
          </button>
          <button
            className={activeTab === 2 ? activeTabClass : tabClass}
            onClick={() => setActiveTab(2)}
          >
            Favorites ({favoriteConversationsFiltered.length})
          </button>
          <button
            className={activeTab === 3 ? activeTabClass : tabClass}
            onClick={() => setActiveTab(3)}
          >
            All ({allConversations.length})
          </button>
        </div>
      </div>
      <ConversationsTable
        rows={gridRows}
        isCompact={hasSelectedConversation}
        onRowClick={handleRowClick}
        isLoading={isLoading}
        isFetchingMore={isFetchingMoreStoredConversations}
        sortBy={sortBy}
        sortDirection={sortDirection}
        onSortChange={handleSortChange}
        renderUGCCell={renderUGCCell}
        renderIPRCell={renderIPRCell}
        getFilterOptions={getFilterOptions}
        onFilterChange={handleFilterChange}
        selectedRowId={selectedRowId}
        checkedRows={checkedRows}
        onCheckboxChange={handleCheckboxChange}
        onToggleFavorite={handleToggleFavorite}
        onLoadMore={handleLoadMore}
      />
    </div>
  );
};

const containerClass = style({
  display: 'flex',
  flexDirection: 'column',
  gap: '12px', 
  padding: '24px',
  backgroundColor: ADALONG_COLORS.SMOOTH_BLACK,
  color: 'white',
  height: '100%',
});

const headerClass = style({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '8px', 
});

const titleClass = style({
  fontSize: '24px',
  fontWeight: 600,
  color: 'white',
  margin: 0,
});

const tabsContainerClass = style({
  borderBottom: `1px solid ${ADALONG_COLORS.DARK_GRAY}`,
  marginBottom: '8px', 
});

const tabsListClass = style({
  display: 'flex',
  gap: '0px',
  backgroundColor: 'transparent',
});

const tabClass = style({
  backgroundColor: 'transparent',
  color: ADALONG_COLORS.LIGHT_GRAY,
  border: 'none',
  padding: '12px 24px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 500,
  borderBottom: '2px solid transparent',
  transition: 'all 0.2s ease',
  $nest: {
    '&:hover': {
      backgroundColor: ADALONG_COLORS.DARKER_GRAY,
      color: 'white',
    },
  },
});

const activeTabClass = style({
  backgroundColor: 'transparent',
  color: ADALONG_COLORS.SMOOTH_WHITE,
  border: 'none',
  padding: '12px 24px',
  cursor: 'pointer',
  fontSize: '14px',
  fontWeight: 600,
  borderBottom: `2px solid ${ADALONG_COLORS.FLASH_BLUE}`,
});

const ugcContainerClass = style({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  height: '100%',
  overflow: 'hidden',
  gap: '4px',
});

const ugcImageClass = style({
  maxHeight: '30px',
  maxWidth: '30px',
  borderRadius: '4px',
  objectFit: 'cover',
});

const errorAlertClass = style({
  backgroundColor: 'rgba(244, 67, 54, 0.1)',
  border: '1px solid rgba(244, 67, 54, 0.3)',
  borderRadius: '8px',
  padding: '16px',
  color: '#ff6b6b',
});

const warningAlertClass = style({
  backgroundColor: 'rgba(255, 152, 0, 0.1)',
  border: '1px solid rgba(255, 152, 0, 0.3)',
  borderRadius: '8px',
  padding: '16px',
  color: '#ff9800',
});

const alertTitleClass = style({
  fontSize: '18px',
  fontWeight: 600,
  marginBottom: '8px',
});

const alertMessageClass = style({
  fontSize: '14px',
  opacity: 0.9,
});

  const subtitleClass = style({
    fontSize: '14px',
    color: '#b0b0b0',
    fontFamily: 'Public Sans, sans-serif',
    lineHeight: '1.4',
  });

  const titleContainerClass = style({
    display: 'flex',
    flexDirection: 'column',})
